"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();

import React, { useState, useEffect } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Menu, MenuItem, IconButton, useMediaQuery } from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import * as Sentry from "@sentry/nextjs"
import styles from "./PricingPage.module.css"
import ZestLifeName from "../../components/common/icons/ZestLifeName"
import FooterSimple from "../../components/common/FooterSimple"
import { UI_STYLE_PRIMARY_COLOR } from "../../lib/constants"
import { formatGroupListingLimitLine } from "../../lib/plans/groupListingLimits"

export default function PricingPage({ season = "Winter" }) {
  const [pricingType, setPricingType] = useState("individual")
  const [anchorEl, setAnchorEl] = useState(null)
  const isMobile = useMediaQuery("(max-width:600px)")
  const open = Boolean(anchorEl)
  const router = useRouter()

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (href) => {
    if (href.startsWith("#")) {
      const target = document.querySelector(href)
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }
    }
    handleMenuClose()
  }

  const handlePlanClick = (planName, planType, planPrice) => {
    // Record plan selection to Sentry
    Sentry.addBreadcrumb({
      message: "Plan selected",
      category: "pricing",
      level: "info",
      data: {
        planName,
        planType,
        planPrice,
        timestamp: new Date().toISOString(),
      },
    })

    Sentry.captureMessage("Plan selected on pricing page", {
      level: "info",
      tags: {
        action: "plan_selected",
        plan_name: planName,
        plan_type: planType,
      },
      extra: {
        plan_name: planName,
        plan_type: planType,
        plan_price: planPrice,
        user_agent: navigator.userAgent,
      },
    })

    // Navigate to signup for most plans, handle Enterprise differently
    if (planName === "Enterprise") {
      window.location.href =
        "mailto:providers@getzestlife.com?subject=Enterprise Plan Inquiry"
    } else {
      router.push("/signup")
    }
  }

  useEffect(() => {
    // Smooth scroll for navigation links
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", (e) => {
        e.preventDefault()
        const href = anchor.getAttribute("href")
        const target = href ? document.querySelector(href) : null
        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        }
      })
    })

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target instanceof HTMLElement) {
          entry.target.style.opacity = "1"
          entry.target.style.transform = "translateY(0)"
        }
      })
    }, observerOptions)

    // Observe all feature cards and pricing cards
    document
      .querySelectorAll(
        `.${styles.featureCard}, .${styles.pricingCard}, .${styles.networkCard}`,
      )
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return
        el.style.opacity = "0"
        el.style.transform = "translateY(20px)"
        el.style.transition = "all 0.6s ease"
        observer.observe(el)
      })

    // Add interactive hover effects
    document
      .querySelectorAll(`.${styles.planButton}, .${styles.networkCard}`)
      .forEach((element) => {
        if (!(element instanceof HTMLElement)) return
        element.addEventListener("mouseenter", () => {
          element.style.transform = "scale(1.05)"
        })
        element.addEventListener("mouseleave", () => {
          element.style.transform = "scale(1)"
        })
      })
  }, [])

  return [
    <div className={styles.root} key="pricing-page">
      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          {/* <div className={styles.logo}>ZestLife</div> */}
          <Link href="/" className={styles.navLink}>
            <ZestLifeName />
          </Link>
          <div className={styles.navLinks}>
            {!isMobile ? (
              <>
                <Link href="/therapists" className={styles.navLink}>
                  For Therapists
                </Link>
                <a href="#pricing" className={styles.navLink}>
                  Pricing
                </a>
                <Link href="/signup" className={styles.ctaButton}>
                  Sign Up
                </Link>
              </>
            ) : (
              <>
                <Link href="/signup" className={styles.ctaButton}>
                  Sign Up
                </Link>
                <IconButton
                  className={styles.menuButton}
                  onClick={handleMenuClick}
                  aria-label="menu"
                  aria-controls={open ? "navigation-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  <MenuIcon className={styles.menuIcon} />
                </IconButton>
                <Menu
                  id="navigation-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  slotProps={{
                    list: {
                      "aria-labelledby": "menu-button",
                    },
                    paper: {
                      className: styles.menuPaper,
                    },
                  }}
                >
                  <MenuItem
                    component={Link}
                    href="/therapists"
                    onClick={handleMenuClose}
                    className={styles.menuItemLink}
                  >
                    For Therapists
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMenuItemClick("#pricing")}
                    className={styles.menuItem}
                  >
                    Pricing
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className={styles.pricing} id="pricing">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Invest in Your Practice's Future</h2>
            <p>Streamline your group work marketing to achieve your growth goals </p>
          </div>

          <div className={styles.pricingToggle}>
            <button
              className={`${styles.toggleButton} ${pricingType === "individual" ? styles.active : ""}`}
              onClick={() => setPricingType("individual")}
            >
              Individual Practice
            </button>
            <button
              className={`${styles.toggleButton} ${pricingType === "organization" ? styles.active : ""}`}
              onClick={() => setPricingType("organization")}
            >
              Organization
            </button>
          </div>

          {/* Individual Pricing */}
          <div
            id="individual-pricing"
            className={styles.pricingGrid}
            style={{ display: pricingType === "individual" ? "grid" : "none" }}
          >
            <div className={styles.pricingCard}>
              <h3 className={styles.planName}>Basic</h3>
              <div className={styles.planPrice}>Free</div>
              <p className={styles.planDescription}>Perfect for getting started</p>
              <ul className={styles.planFeatures}>
                <li>{formatGroupListingLimitLine("basic")}</li>
                <li>Client interest collection</li>
                <li>Standard search placement</li>
                <li>1 professional provider profile</li>
                <li>Store meeting reflections</li>
              </ul>
              <button
                className={styles.planButton}
                onClick={() => handlePlanClick("Basic", "individual", "Free")}
              >
                Start Free
              </button>
            </div>

            <div className={`${styles.pricingCard} ${styles.featured}`}>
              <h3 className={styles.planName}>Pro</h3>
              <div className={styles.planPrice}>
                $5
                <span
                  style={
                    {
                      // textDecoration: "line-through",
                      // color: "#999",
                      // fontSize: "1.5rem",
                    }
                  }
                >
                  /mo
                </span>
              </div>
              <div
                style={{
                  fontSize: "0.9rem",
                  color: UI_STYLE_PRIMARY_COLOR,
                  marginTop: "0.5rem",
                  marginBottom: "2rem",
                }}
              >
                {`${season} sign up deal! (Typically $19/mo)`}
              </div>
              {/* <p className={styles.planDescription}>
                Elevate your group work marketing
              </p> */}
              <ul className={styles.planFeatures}>
                <li>Everything in Basic</li>
                <li>{formatGroupListingLimitLine("individual_pro")}</li>
                <li>Profile and group listing feedback</li>
                <li>Social links in profile</li>
                <li>Add short introduction videos to your listings</li>
                <li>Verified badge</li>
                <li>Priority support</li>
                <li>$50/year (save $10)</li>
              </ul>
              <button
                className={styles.planButton}
                onClick={() => handlePlanClick("Pro", "individual", "$5/mo")}
              >
                Start Pro Trial
              </button>
            </div>

            <div className={styles.pricingCard}>
              <h3 className={styles.planName}>Accelerate</h3>
              <div className={styles.planPrice}>
                $15<span>/mo</span>
              </div>
              <p className={styles.planDescription}>
                Build momentum to launch or re-launch the groups you‘d love to offer
              </p>
              <ul className={styles.planFeatures}>
                <li>Everything in Pro</li>
                <li>{formatGroupListingLimitLine("individual_accelerate")}</li>
                <li>1 collaborative blog article per quarter</li>
                <li>Priority support</li>
                <li>Beta feature access</li>
                <li>$150/year (save $30)</li>
              </ul>
              <button
                className={styles.planButton}
                onClick={() => handlePlanClick("Accelerate", "individual", "$15/mo")}
              >
                Start Accelerate Trial
              </button>
            </div>
          </div>

          {/* Organization Pricing */}
          <div
            id="organization-pricing"
            className={styles.pricingGrid}
            style={{ display: pricingType === "organization" ? "grid" : "none" }}
          >
            <div className={styles.pricingCard}>
              <h3 className={styles.planName}>Starter</h3>
              <div className={styles.planPrice}>
                $15<span>/mo</span>
              </div>
              <p className={styles.planDescription}>
                For small teams advertising or streamlining their groups
              </p>
              <ul className={styles.planFeatures}>
                <li>{formatGroupListingLimitLine("org_starter")}</li>
                <li>Branded organization profile</li>
                <li>Shared admin dashboard</li>
                <li>Priority support</li>
                <li>Beta feature access</li>
                <li>$150/year (save $30)</li>
              </ul>
              <button
                className={styles.planButton}
                onClick={() => handlePlanClick("Starter", "organization", "$15/mo")}
              >
                Start Starter
              </button>
            </div>

            <div className={`${styles.pricingCard} ${styles.featured}`}>
              <h3 className={styles.planName}>Growth</h3>
              <div className={styles.planPrice}>
                $30<span>/mo</span>
              </div>
              <p className={styles.planDescription}>
                For medium sized teams expanding their group work reach or enhancing
                their group care offerings
              </p>
              <ul className={styles.planFeatures}>
                <li>{formatGroupListingLimitLine("org_growth")}</li>
                <li>Unlimited cohorts per group</li>
                <li>Branded organization profile</li>
                <li>Shared admin dashboard</li>
                <li>Profile and group listing feedback</li>
                <li>Up to 3 collaborative blog articles per year</li>
                <li>Priority support</li>
                <li>Beta feature access</li>
                <li>$300/year (save $60)</li>
              </ul>
              <button
                className={styles.planButton}
                onClick={() => handlePlanClick("Growth", "organization", "$30/mo")}
              >
                Start Growth
              </button>
            </div>

            <div className={styles.pricingCard}>
              <h3 className={styles.planName}>Enterprise</h3>
              <div className={styles.planPrice}>Custom</div>
              <p className={styles.planDescription}>
                For large teams or organizations reach out for custom packages to see
                how we can support your group care offerings
              </p>
              <ul className={styles.planFeatures}>
                <li>Everything in Growth</li>
                <li>15+ groups</li>
                <li>Custom packages</li>
                <li>Custom active groups</li>
                <li>Social media content generation</li>
                <li>Managed marketing spend</li>
              </ul>
              <button
                className={styles.planButton}
                onClick={() =>
                  handlePlanClick("Enterprise", "organization", "Custom")
                }
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Provider Network Section */}
      <section className={styles.providerNetwork}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Join the ZestLife Provider Network</h2>
            <p>Connect, collaborate, and create seamless care for your clients</p>
          </div>

          <div className={styles.networkGrid}>
            <div className={styles.networkCard}>
              <div className={styles.networkIcon}>📰</div>
              <h3>Curated Newsletter</h3>
              <p>
                Listservs can be overwhelming. Our monthly newsletter makes it
                simple, with a clean, curated list of upcoming virtual or in-person
                groups in your area.
              </p>
            </div>

            <div className={styles.networkCard}>
              <div className={styles.networkIcon}>🔍</div>
              <h3>Effortless Referrals</h3>
              <p>
                No scattered links or endless digging. Search groups from independent
                providers and practices across the country easily in one place, and
                filter by location, setting, or keywords to find the right fit
                quickly.
              </p>
            </div>

            <div className={styles.networkCard}>
              <div className={styles.networkIcon}>🤝</div>
              <h3>Professional Network</h3>
              <p>
                Expand your colleague community. Message providers directly on
                platform to organize your group work connections, share referrals,
                and build relationships that support your practice.
              </p>
            </div>
          </div>

          <div className={styles.networkCta}>
            <Link href="/signup" className={styles.primaryButton}>
              Join the Network Free
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.features} id="features">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Everything You Need to Thrive</h2>
            <p>From your first group to scaling multiple locations</p>
          </div>
          <div className={styles.featuresGrid}>
            {/* <div className={styles.featureCard}>
              <div className={styles.featureIcon}>📊</div>
              <h3>Smart Group Management</h3>
              <p>
                Effortlessly manage multiple groups, track attendance, and monitor
                progress. Track waitlist clients and group fill rates to optimize
                your practice's performance.
              </p>
            </div> */}
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🎯</div>
              <h3>Customized Marketing Support</h3>
              <p>
                Strengthen your online presence with our seasonal marketing package.
                Get support creating media content—whether you’re short on time,
                building momentum for a launch or re-launch, or looking for a
                polished profile and group listing that resonates with the right
                members.
              </p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>👥</div>
              <h3>Team Collaboration</h3>
              <p>
                Enable your administrative staff to manage listings and handle
                inquiries, while featuring multiple providers under your
                practice—making it simple to stay organized and consistent across
                groups, facilitators, and locations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>Ready to Transform Your Practice?</h2>
          <p>
            Join mental health and wellness professionals across the country in
            making group work a priority
          </p>
          <div className={styles.heroButtons}>
            <Link href="/signup" className={styles.primaryButton}>
              Start Free Today
            </Link>
            <a
              href="mailto:providers@getzestlife.com"
              className={styles.secondaryButton}
            >
              Schedule a Demo
            </a>
          </div>
          <p className={styles.ctaNote}>
            Basic plan free • No credit card required • Upgrade or cancel anytime
          </p>
        </div>
      </section>
    </div>,
    <FooterSimple key="footer" />,
  ]
}
